import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setActiveTunnelIndex } from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';

interface TunnelIndexesProps {
  tunnels: Array<{ id: number; index?: string | null }>;
}

/**
 * Phase 6b — #tunnels-indexes pills (tmpl/canvas/tunnel.indexes.tmpl §2.3,
 * re-homed from the canvas's jQuery render): `all` pill + one per tunnel
 * showing tunnel.index ("1:1"), `.active` from the slice (active_id —
 * 'all' when none isolated). Rendered only when ≥2 tunnels AND the canvas
 * has asked for them (renderTunnelsIndexes → tunnelIndexesVisible).
 *
 * Clicks are the MainView contract (§2.4 changeIndex): `.active` swap +
 * `canvas:change:tunnel:index (id, prev_id)` on the bus — the 6b canvas
 * consumes it (changeIndex → hideOrShowTunnels over the 6c registries).
 */
const TunnelIndexes = ({ tunnels }: TunnelIndexesProps) => {
  const dispatch = useAppDispatch();
  const visible = useAppSelector((s) => s.ahuBoard.canvas.tunnelIndexesVisible);
  const activeId = useAppSelector((s) => s.ahuBoard.canvas.activeTunnelIndexId);

  if (!visible || tunnels.length < 2) return <div id="tunnels-indexes" />;

  // MainView.changeIndex (main.js:116): fires even on the already-active pill
  // (re-click of 'all'/same tunnel re-runs hideOrShowTunnels) — kept verbatim.
  const onPillClick = (id: number | 'all') => {
    const prev = activeId;
    dispatch(setActiveTunnelIndex(id));
    ahuBoardBus.emit('canvas:change:tunnel:index', id, prev);
  };

  return (
    <div id="tunnels-indexes">
      <div
        data-id="all"
        className={`tunnel-index${activeId === 'all' ? ' active' : ''}`}
        onClick={() => onPillClick('all')}
      >
        all
      </div>
      {tunnels.map((t) => (
        <div
          key={t.id}
          data-id={t.id}
          className={`tunnel-index${activeId === t.id ? ' active' : ''}`}
          onClick={() => onPillClick(t.id)}
        >
          {t.index}
        </div>
      ))}
    </div>
  );
};

export default TunnelIndexes;
